import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {pluralize} from 'BenchlingUtil';
import {isNumericField, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {LEGACY_FONT_PROXIMA_13} from 'styles/FontConstants';
import {lrSplit} from 'styles/Utilities';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY, GRAY_DARK, GRAY_LIGHT, RED_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import MenuPopover from 'ui-library/menus/MenuPopover';

import FilterLookupStepForm, {FilterLookupStepFormErrors} from './FilterLookupStepForm';
import {FilterLookupStepInfo, LookupStepInfo, LookupStepType} from './LookupConfigModalConstants';
import {getSchemaFieldsForLookupStep} from './LookupConfigUtils';
import {getLookupStepInfoForFieldStepItem} from './LookupStepInitializerUtils';

interface FilterLookupStepPopoverProps {
  className?: string;
  disabled?: boolean;
  hasError?: boolean;
  parentSchema: Schema;
  filterLookupSteps: Array<FilterLookupStepInfo>;
  handleSetFilterLookupSteps: (lookupStepInfos: Array<LookupStepInfo>) => void;
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean;
}

function validateFilterStep(filterLookupStepInfo: FilterLookupStepInfo): FilterLookupStepFormErrors {
  const schemaField = filterLookupStepInfo.schemaField;
  return {
    schemaField: schemaField == null,
    filterValue: schemaField
      ? isNumericField(schemaField) && typeof filterLookupStepInfo.lookupStep.value !== 'number'
      : false,
  };
}

function validateFilterLookupSteps(
  filterLookupSteps: Array<FilterLookupStepInfo>
): Array<FilterLookupStepFormErrors> {
  return filterLookupSteps.map((step) => validateFilterStep(step));
}

function isErrorsEmpty(errors: Array<FilterLookupStepFormErrors>): boolean {
  return errors.every((stepErrors) => Object.values(stepErrors).every((value) => !value));
}

function AddFilterButton(props: {handleAddFilterLookupStep: () => void}): JSX.Element {
  return (
    <span className={classNames(css(styles.addFilter))} onClick={props.handleAddFilterLookupStep}>
      <BenchlingIcon className={css(styles.addIcon)} icon="search-filter-add" />
      <span>Add filter</span>
    </span>
  );
}

export default function FilterLookupStepPopover(props: FilterLookupStepPopoverProps): JSX.Element | null {
  const [isOpen, setIsOpen] = useState(false);
  const [errors, setErrors] = useState(validateFilterLookupSteps(props.filterLookupSteps));
  const schemaFields = getSchemaFieldsForLookupStep(props.parentSchema, false, props.schemaFieldFilterFn);
  if (schemaFields.length === 0 && props.filterLookupSteps.length === 0) {
    return null;
  }
  return (
    <Dropdown
      isOpen={isOpen}
      onToggle={setIsOpen}
      closeOnInsideClick={false}
      closeOnOutsideClick={true}
      menu={
        <MenuPopover
          className={css(styles.menu)}
          dataTestAttrs={{component: 'FilterLookupStepPopover', element: 'wrapper'}}
          width={MenuWidth.FULL}
        >
          <div className={css(styles.title)}>Configure Filter</div>
          {props.filterLookupSteps.length ? (
            props.filterLookupSteps.map((filterLookupStep, i) => (
              <div key={i} className={css(styles.formContainer)}>
                <FilterLookupStepForm
                  lookupStepInfo={filterLookupStep}
                  onChange={(newStep) => {
                    const newFilterLookupSteps = [...props.filterLookupSteps];
                    newFilterLookupSteps[i] = newStep;
                    props.handleSetFilterLookupSteps(newFilterLookupSteps);
                  }}
                  onRemove={() => {
                    props.handleSetFilterLookupSteps([
                      ...props.filterLookupSteps.slice(0, i),
                      ...props.filterLookupSteps.slice(i + 1),
                    ]);
                  }}
                  disabled={props.disabled}
                  errors={errors[i]}
                  fieldOptions={schemaFields}
                />
              </div>
            ))
          ) : (
            <div className={css(styles.noActiveFilters)}>No active filters</div>
          )}
          <div className={css(styles.footer)}>
            <AddFilterButton
              handleAddFilterLookupStep={() => {
                const newFilterLookupStep = getLookupStepInfoForFieldStepItem(
                  LookupStepType.FILTER,
                  schemaFields[0],
                  props.parentSchema
                ) as FilterLookupStepInfo;
                const newFilterLookupSteps = [...props.filterLookupSteps, newFilterLookupStep];
                const newErrors = validateFilterLookupSteps(newFilterLookupSteps);
                setErrors(newErrors);
                props.handleSetFilterLookupSteps(newFilterLookupSteps);
              }}
            />
            <Button
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyle.SECONDARY}
              text="Done"
              onClick={() => {
                const newErrors = validateFilterLookupSteps(props.filterLookupSteps);
                setErrors(newErrors);
                if (isErrorsEmpty(newErrors)) {
                  setIsOpen(false);
                }
              }}
            />
          </div>
        </MenuPopover>
      }
    >
      <Button
        className={classNames(
          css(
            styles.filterButton,
            props.disabled && styles.filterButtonDisabled,
            !isErrorsEmpty(errors) && styles.filterButtonHasError
          ),
          props.className
        )}
        disabled={props.disabled}
        icon="filter-menu"
        text={
          props.filterLookupSteps.length > 0
            ? `${props.filterLookupSteps.length} ${pluralize(props.filterLookupSteps.length, 'filter')}`
            : 'Filter'
        }
        size={ButtonSize.SMALL}
        buttonStyle={ButtonStyle.SECONDARY_WHITE}
      />
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  menu: {
    ...LEGACY_FONT_PROXIMA_13,
    width: 400,
  },
  title: {
    padding: '12px',
  },
  filterButton: {
    color: BLUE_LIGHT,
    fontWeight: 600,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },
  filterButtonDisabled: {
    color: GRAY,
  },
  filterButtonHasError: {
    color: RED_LIGHT,
  },
  footer: {
    borderTopColor: GRAY_LIGHT,
    borderTopStyle: 'solid',
    borderTopWidth: 1,
    padding: 10,
    ...lrSplit,
  },
  addFilter: {
    color: BLUE_LIGHT,
    cursor: 'pointer',
  },
  addIcon: {
    height: 5,
    marginRight: 10,
    width: 5,
  },
  formContainer: {
    borderBottomColor: GRAY_LIGHT,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    padding: 10,
  },
  noActiveFilters: {
    color: GRAY_DARK,
    padding: '14px 12px',
  },
});
